import { jsPDF } from 'jspdf'
import { AudienceParams, AudienceDatum } from '@/pdf/types'
import { renderHtmlToImage, getLogoDataUrl } from '@/pdf/htmlRenderer'

// Cores
const PAGE_BG_CSS = 'rgb(250,250,251)'
// Dimensões dos SVGs (alta resolução para PDF)
const BAR_SVG_W = 1360
const BAR_SVG_H = 560
const PIE_SVG_SIZE = 600

// ─── Configuração de duração ──────────────────────────────────────────────────

const DURACAO_CATEGORIAS = [
    { faixa: '0-20 minutos',  range: [0, 2] as [number, number] },
    { faixa: '20-30 minutos', range: [2, 3] as [number, number] },
    { faixa: '30-40 minutos', range: [3, 4] as [number, number] },
    { faixa: '40-50 minutos', range: [4, 5] as [number, number] },
    { faixa: '50-60 minutos', range: [5, 6] as [number, number] },
    { faixa: '60+ minutos',   range: [6, Infinity] as [number, number] },
]

function calcularFaixasDuracao(durationsData: { duration: string; count: string }[]) {
    const buckets = DURACAO_CATEGORIAS.map(c => ({ ...c, total: 0 }))
    for (const item of durationsData) {
        const dur = parseInt(item.duration, 10)
        const cnt = parseInt(item.count, 10)
        for (const bucket of buckets) {
            if (dur >= bucket.range[0] && dur < bucket.range[1]) {
                bucket.total += cnt
                break
            }
        }
    }
    const totalSessoes = buckets.reduce((s, b) => s + b.total, 0)
    return buckets.map(b => ({
        faixa:      b.faixa,
        sessoes:    b.total,
        percentual: totalSessoes > 0 ? (b.total / totalSessoes) * 100 : 0,
    }))
}

const PIE_COLORS_HEX = ['#5166dc', '#00AB55', '#DD962E', '#21ccc7', '#922f2b','#95cc41',]

function formatDateLabel(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const hour = date.getHours().toString().padStart(2, '0')
    const minute = date.getMinutes().toString().padStart(2, '0')
    return `${day}/${month} ${hour}:${minute}`
}

// Agrupa os dados em janelas de 6h somando as sessões
function agruparEm6h(dataset: AudienceDatum[]): { label: string; sessions: number }[] {
    const buckets = new Map<string, number>()

    for (const { timestamp, sessions } of dataset) {
        const d = new Date(timestamp)
        const bucketHour = Math.floor(d.getHours() / 6) * 6
        const key = new Date(d.getFullYear(), d.getMonth(), d.getDate(), bucketHour, 0, 0).toISOString()
        buckets.set(key, (buckets.get(key) ?? 0) + sessions)
    }

    return Array.from(buckets.entries())
        .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
        .map(([key, total]) => ({ label: formatDateLabel(new Date(key)), sessions: total }))
}

function escapeXml(str: unknown): string {
    const s = String(str ?? '')
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

// Converte SVG string em PNG data URL via canvas off-screen
function svgToDataURL(svgStr: string, width: number, height: number): Promise<string> {
    return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            canvas.getContext('2d')!.drawImage(img, 0, 0)
            resolve(canvas.toDataURL('image/png'))
        }
        img.onerror = () => resolve('')
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgStr)
    })
}

// ─── Bar chart (SVG) ──────────────────────────────────────────────────────────

function buildBarChartSVG(labels: string[], values: number[]): string {
    const W = BAR_SVG_W
    const H = BAR_SVG_H
    const padLeft = 70
    const padRight = 20
    const padTop = 20
    const padBottom = 110

    const chartW = W - padLeft - padRight
    const chartH = H - padTop - padBottom

    const maxGrid = Math.max(Math.ceil(Math.max(...values, 1) / 10) * 10, 10)
    const n = labels.length

    const barGroupW = chartW / n
    const barW = Math.min(barGroupW * 0.55, 70)
    const barOffsetX = (barGroupW - barW) / 2

    const ySteps = 5
    let gridLines = ''
    let yLabels = ''
    for (let i = 0; i <= ySteps; i++) {
        const val = Math.round((maxGrid / ySteps) * i)
        const y = padTop + chartH - (val / maxGrid) * chartH
        gridLines += `<line x1="${padLeft}" y1="${y.toFixed(1)}" x2="${(W - padRight).toFixed(1)}" y2="${y.toFixed(1)}" stroke="#eeeeee" stroke-width="1.5" stroke-dasharray="4 3"/>`
        yLabels += `<text x="${(padLeft - 8).toFixed(1)}" y="${(y + 5).toFixed(1)}" text-anchor="end" fill="#777777" font-size="20" font-family="Arial,sans-serif">${val}</text>`
    }

    let bars = ''
    let xLabels = ''
    for (let i = 0; i < n; i++) {
        const xBase = padLeft + i * barGroupW + barOffsetX
        const barH = Math.max((values[i] / maxGrid) * chartH, 0)
        const yTop = padTop + chartH - barH

        bars += `<rect x="${xBase.toFixed(1)}" y="${yTop.toFixed(1)}" width="${barW.toFixed(1)}" height="${barH.toFixed(1)}" fill="#2e5eaa" rx="3" ry="3"/>`

        const lx = padLeft + i * barGroupW + barGroupW / 2
        const ly = padTop + chartH + 14
        xLabels += `<text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" text-anchor="end" fill="#777777" font-size="20" font-family="Arial,sans-serif" transform="rotate(-45,${lx.toFixed(1)},${ly.toFixed(1)})">${escapeXml(labels[i])}</text>`
    }

    const axisV = `<line x1="${padLeft}" y1="${padTop}" x2="${padLeft}" y2="${(padTop + chartH).toFixed(1)}" stroke="#cccccc" stroke-width="2"/>`
    const axisH = `<line x1="${padLeft}" y1="${(padTop + chartH).toFixed(1)}" x2="${(W - padRight).toFixed(1)}" y2="${(padTop + chartH).toFixed(1)}" stroke="#cccccc" stroke-width="2"/>`

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}"><rect width="${W}" height="${H}" fill="white"/>${gridLines}${axisV}${axisH}${bars}${yLabels}${xLabels}</svg>`
}

// ─── Pie chart (SVG) ──────────────────────────────────────────────────────────

function pieArcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
    const sweep = Math.min(a1 - a0, 2 * Math.PI - 0.0001)
    const end = a0 + sweep
    const x1 = cx + r * Math.cos(a0)
    const y1 = cy + r * Math.sin(a0)
    const x2 = cx + r * Math.cos(end)
    const y2 = cy + r * Math.sin(end)
    return `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${sweep > Math.PI ? 1 : 0} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`
}

function buildPieChartSVG(faixas: { faixa: string; sessoes: number; percentual: number }[]): string {
    const SIZE = PIE_SVG_SIZE
    const cx = SIZE / 2
    const cy = SIZE / 2
    const r = SIZE * 0.43

    // Filtrar faixas sem sessões
    const activeFaixas = faixas.filter(f => f.sessoes > 0 || f.percentual > 0)
    const total = activeFaixas.reduce((a, f) => a + f.sessoes, 0)

    // Caso especial: slice único (100%) — arc de 2π é inválido em SVG, usar circle
    if (activeFaixas.length === 1) {
        const color = PIE_COLORS_HEX[0]
        return `<svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}"><rect width="${SIZE}" height="${SIZE}" fill="${PAGE_BG_CSS}"/><circle cx="${cx}" cy="${cy}" r="${r}" fill="${color}"/><text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central" fill="white" font-size="28" font-weight="bold" font-family="Arial,sans-serif">100%</text></svg>`
    }

    let slices = ''
    let pctLabels = ''
    let angle = -Math.PI / 2  // começa às 12h

    activeFaixas.forEach((f, i) => {
        const pct = total > 0 ? f.sessoes / total : f.percentual / 100
        const sweep = pct * 2 * Math.PI
        const color = PIE_COLORS_HEX[i % PIE_COLORS_HEX.length]

        slices += `<path d="${pieArcPath(cx, cy, r, angle, angle + sweep)}" fill="${color}"/>`

        if (pct >= 0.05) {
            const mid = angle + sweep / 2
            const labelR = r * 0.65
            const lx = cx + labelR * Math.cos(mid)
            const ly = cy + labelR * Math.sin(mid)
            pctLabels += `<text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" text-anchor="middle" dominant-baseline="central" fill="white" font-size="28" font-weight="bold" font-family="Arial,sans-serif">${(pct * 100).toFixed(1)}%</text>`
        }

        angle += sweep
    })

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}"><rect width="${SIZE}" height="${SIZE}" fill="${PAGE_BG_CSS}"/>${slices}${pctLabels}</svg>`
}

// ─── Exporters ────────────────────────────────────────────────────────────────

function gerarImagemBarras(labels: string[], values: number[]): Promise<string> {
    return svgToDataURL(buildBarChartSVG(labels, values), BAR_SVG_W, BAR_SVG_H)
}

function gerarImagemPie(faixas: { faixa: string; sessoes: number; percentual: number }[]): string {
    const activeFaixas = faixas
        .map((f, i) => ({ ...f, origIdx: i }))
        .filter(f => f.sessoes > 0 || f.percentual > 0)
    if (activeFaixas.length === 0) return ''

    const SIZE = PIE_SVG_SIZE
    const canvas = document.createElement('canvas')
    canvas.width = SIZE
    canvas.height = SIZE
    const ctx = canvas.getContext('2d')!

    const cx = SIZE / 2
    const cy = SIZE / 2
    const r = SIZE * 0.43
    const total = activeFaixas.reduce((a, f) => a + f.sessoes, 0)

    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, SIZE, SIZE)

    let angle = -Math.PI / 2
    activeFaixas.forEach((f) => {
        const pct = total > 0 ? f.sessoes / total : f.percentual / 100
        const sweep = pct * 2 * Math.PI

        ctx.beginPath()
        ctx.moveTo(cx, cy)
        ctx.arc(cx, cy, r, angle, angle + sweep)
        ctx.closePath()
        ctx.fillStyle = PIE_COLORS_HEX[f.origIdx % PIE_COLORS_HEX.length]
        ctx.fill()

        if (pct >= 0.05) {
            const mid = angle + sweep / 2
            const lr = r * 0.575
            ctx.fillStyle = 'white'
            ctx.font = 'bold 28px Arial, sans-serif'
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.fillText(`${(pct * 100).toFixed(1)}%`, cx + lr * Math.cos(mid), cy + lr * Math.sin(mid))
        }

        angle += sweep
    })

    return canvas.toDataURL('image/png')
}

// ─── HTML page builder ────────────────────────────────────────────────────────

function buildAudienceHtml(
    params: AudienceParams,
    barImg: string | null,
    pieFaixas?: { faixa: string; sessoes: number; percentual: number }[],
    pieImg?: string,
    logoSrc?: string,
): string {
    const agora = new Date()
    const dataGerado = agora.toLocaleDateString('pt-BR')
    const horaGerado = agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    const nomeRadio = params.companyName || ''
    let barSection = ''
    if (barImg) {
        barSection = `
<div style="margin-bottom:24px;">
    <!-- Título com linha azul -->
    <div style="margin-bottom:10px;">
        <div style="display:inline-block;font-size:18px;font-weight:bold;color:#2e5eaa;padding-bottom:3px;">
            Audiência
        </div>
        <div style="width:57px;height:3px;background:#2e5eaa;border-radius:2px;margin-top:2px;"></div>
    </div>

    <!-- Gráfico -->
    <img src="${barImg}" style="width:680px;height:219px;display:block;border-radius:8px;" />
</div>`
    }

    // Insight + recomendação do comportamento de audiência
    const barInsight = params.comportamento_audiencia?.insight || params.audience_insight || params.resumo_executivo?.frase_destaque
    if (barImg && barInsight) {
        barSection += `
        <div style="background:#e5eaf5;border-radius:8px;padding:10px 14px 10px 18px;position:relative;margin-top:-8px;margin-bottom:10px;">
            <div style="position:absolute;left:0;top:0;bottom:0;width:6px;background:#2e5eaa;border-radius:8px 0 0 8px;"></div>
            <p style="font-size:12px;color:#32405a;font-style:italic;margin:0;line-height:1.5;">${escapeXml(barInsight)}</p>
        </div>`
    }

    // ── Seção de pizza (duração) ──
    let pieSection = ''
    if (pieFaixas && pieFaixas.length > 0 && pieImg) {
        const total = pieFaixas.reduce((a, f) => a + f.sessoes, 0)
        const legendItems = pieFaixas.map((f, i) => {
            const pct = total > 0 ? ((f.sessoes / total) * 100).toFixed(1) : f.percentual.toFixed(1)
            return `
            <div style="display:flex;align-items:center;margin-bottom:8px;">
                <div style="width:15px;height:15px;min-width:15px;background:${PIE_COLORS_HEX[i % PIE_COLORS_HEX.length]};border-radius:4px;margin-right:8px;"></div>
                <span style="font-size:13px;color:#555555;font-family:Arial,sans-serif;">${escapeXml(f.faixa)}&nbsp;<strong>${pct}%</strong></span>
            </div>`
        }).join('')

        const durationInsight = params.distribuicao_duracao?.insight

        pieSection = `
        <div style="background:#ffffff;border-radius:8px;box-shadow:0 3px 8px rgba(0,0,0,0.10);padding:19px;box-sizing:border-box;margin-top:16px;">
            <div style="margin-bottom:15px;">
                <div style="font-size:19px;font-weight:600;color:#2e5eaa;font-family:Arial,sans-serif;padding-bottom:4px;">Tempo m&eacute;dio de audi&ecirc;ncia por intervalo</div>
                <div style="width:57px;height:3px;background:#2e5eaa;border-radius:2px;"></div>
            </div>
            <div style="display:flex;align-items:center;width:100%;">
                <div style="width:58%;display:flex;justify-content:center;align-items:center;">
                    <img src="${pieImg}" style="width:220px;height:220px;border-radius:8px;"/>
                </div>
                <div style="width:42%;padding-left:19px;display:flex;flex-direction:column;justify-content:center;">
                    ${legendItems}
                </div>
            </div>
        </div>
        ${durationInsight ? `
        <div style="background:#e5eaf5;border-radius:8px;padding:10px 14px 10px 18px;position:relative;margin-top:12px;">
            <div style="position:absolute;left:0;top:0;bottom:0;width:6px;background:#2e5eaa;border-radius:8px 0 0 8px;"></div>
            <p style="font-size:12px;color:#32405a;font-style:italic;margin:0;line-height:1.5;">${escapeXml(durationInsight)}</p>
        </div>` : ''}
        `
    }

    return `<div style="width:794px;height:1122px;background:#fafafa;font-family:Arial,sans-serif;padding:83px 57px 40px;box-sizing:border-box;position:relative;">
        ${logoSrc ? `<img src="${logoSrc}" style="position:absolute;top:20px;right:57px;height:22px;" />` : ''}
        ${barSection}
        ${pieSection}
        <div style="position:absolute;bottom:24px;left:57px;right:57px;text-align:center;">
            <span style="font-size:9px;color:#a0a0a0;">Relat&oacute;rio gerado em ${dataGerado} &agrave;s ${horaGerado}${nomeRadio ? ` - ${nomeRadio}` : ''}</span>
        </div>
    </div>`
}

// ─── Page entry point ─────────────────────────────────────────────────────────

export async function addAudiencePage(pdf: jsPDF, parametros: AudienceParams): Promise<void> {
    const dataset: AudienceDatum[] = parametros.audience_data || []
    const hasBars = dataset.length > 0

    // Calcular faixas de duração
    const rawDurations = parametros.durations_data
    const pieFaixas = rawDurations?.length ? calcularFaixasDuracao(rawDurations) : undefined
    const hasPie = pieFaixas && pieFaixas.some(f => f.sessoes > 0)

    if (!hasBars && !hasPie) return

    let barImg: string | null = null
    if (hasBars) {
        const grouped = agruparEm6h(dataset)
        if (grouped.length > 0) {
            barImg = await gerarImagemBarras(grouped.map(g => g.label), grouped.map(g => g.sessions))
        }
    }

    let pieImg: string | undefined
    if (hasPie) {
        pieImg = gerarImagemPie(pieFaixas!) || undefined
    }

    const logoSrc = await getLogoDataUrl()
    const img = await renderHtmlToImage(buildAudienceHtml(parametros, barImg, pieFaixas, pieImg, logoSrc))
    pdf.addPage()
    pdf.addImage(img, 'JPEG', 0, 0, 210, 297)
}

